import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TopToolCard } from './TopToolCard';
import { useTools } from '../hooks/useTools';
export function TopToolsSection() {
  const {
    tools,
    loading
  } = useTools();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  // ONLY show scraped tools (not curated/admin tools)
  const scrapedTools = tools.filter(t => (t as any).source === 'scraped');
  // Filter out Jasper, Grammarly, and Sudowrite
  const excludedTools = ['jasper', 'grammarly', 'sudowrite'];
  const filteredTools = scrapedTools.filter(t => !excludedTools.some(excluded => t.name.toLowerCase().includes(excluded)));
  // Get top 6 featured scraped tools (2 slides of 3)
  const featuredTools = filteredTools.filter(t => t.is_featured).slice(0, 6);
  const displayTools = featuredTools.length >= 6 ? featuredTools : filteredTools.slice(0, 6);
  // Responsive: 1 tool on mobile, 3 on desktop
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const toolsPerSlide = isMobile ? 1 : 3;
  const totalSlides = Math.ceil(displayTools.length / toolsPerSlide);
  const slides = Array.from({
    length: totalSlides
  }, (_, i) => displayTools.slice(i * toolsPerSlide, (i + 1) * toolsPerSlide));
  const nextSlide = () => {
    setDirection(1);
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };
  const prevSlide = () => {
    setDirection(-1);
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };
  const goToSlide = (index: number) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  };
  if (loading) {
    return <section className="py-16 px-4 max-w-7xl mx-auto" data-id="element-709">
        <div className="text-center py-20" data-id="element-710">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent" data-id="element-711"></div>
          <p className="text-gray-400 mt-4" data-id="element-712">Loading top tools...</p>
        </div>
      </section>;
  }
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -1000 : 1000,
      opacity: 0
    })
  };
  return <section className="py-16 px-4 max-w-7xl mx-auto" data-id="element-713">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4" data-id="element-714">
        <div data-id="element-715">
          <h2 className="text-3xl md:text-4xl text-white mb-3 font-subtitle" data-id="element-716">
            Top AI Tools
          </h2>
          <p className="text-gray-400 text-sm max-w-xl" data-id="element-717">
            The most popular and powerful AI tools trusted by millions of users
            worldwide.
          </p>
        </div>

        {/* Navigation Arrows - Top Right (Desktop) */}
        <div className="hidden md:flex items-center gap-2" data-id="element-718">
          <button onClick={prevSlide} className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-500/30 transition-all group" aria-label="Previous slide" data-id="element-719">
            <ChevronLeft size={20} className="text-gray-400 group-hover:text-cyan-400 transition-colors" data-id="element-720" />
          </button>
          <button onClick={nextSlide} className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-500/30 transition-all group" aria-label="Next slide" data-id="element-721">
            <ChevronRight size={20} className="text-gray-400 group-hover:text-cyan-400 transition-colors" data-id="element-722" />
          </button>
        </div>
      </div>

      {/* Carousel Container */}
      <div className="relative" data-id="element-723">
        {/* Slides */}
        <div className="overflow-hidden" data-id="element-724">
          <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-725">
            <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            x: {
              type: 'spring',
              stiffness: 300,
              damping: 30
            },
            opacity: {
              duration: 0.2
            }
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-726">
              {slides[currentSlide]?.map((tool, index) => <motion.div key={tool.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: index * 0.05
            }} className="h-full" data-id="element-727">
                  <TopToolCard tool={tool} data-id="element-728" />
                </motion.div>)}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Dots */}
        <div className="flex items-center justify-center gap-2 mt-8" data-id="element-729">
          {Array.from({
          length: totalSlides
        }).map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`transition-all ${index === currentSlide ? 'w-8 h-2 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full' : 'w-2 h-2 bg-white/20 rounded-full hover:bg-white/40'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-730" />)}
        </div>

        {/* Mobile Navigation Buttons */}
        <div className="flex md:hidden items-center justify-center gap-4 mt-6" data-id="element-731">
          <button onClick={prevSlide} className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-500/30 transition-all" aria-label="Previous slide" data-id="element-732">
            <ChevronLeft size={20} className="text-gray-400" data-id="element-733" />
          </button>
          <span className="text-sm text-gray-400" data-id="element-734">
            {currentSlide + 1} / {totalSlides}
          </span>
          <button onClick={nextSlide} className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-500/30 transition-all" aria-label="Next slide" data-id="element-735">
            <ChevronRight size={20} className="text-gray-400" data-id="element-736" />
          </button>
        </div>
      </div>

      {displayTools.length === 0 && <div className="text-center py-20 text-gray-500" data-id="element-737">
          No tools available yet.
        </div>}
    </section>;
}